import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const DAY_OPTIONS = [
    { value: 'Senin', label: 'Senin' },
    { value: 'Selasa', label: 'Selasa' },
    { value: 'Rabu', label: 'Rabu' },
    { value: 'Kamis', label: 'Kamis' },
    { value: 'Jumat', label: 'Jumat' },
    { value: 'Sabtu', label: 'Sabtu' },
];

const EnrollmentBookingFields = ({
    cityList,
    learningTypeList,
    programList,
    classMeetList,
    showRegularMeetFields = true,
    omitBasicsFields = false,
    effectiveTypeCourse,
    selectedCourseTitle = '',
    meetItems,
    selectedItem,
    onSelectItem,
    isStepsComplete = false,
    showPrice
}) => {
    const {
        register,
        control,
        setValue,
        getValues,
        clearErrors,
        formState: { errors },
    } = useFormContext();

    const cityId = useWatch({ control, name: 'cityId' });
    const learningType = useWatch({ control, name: 'learningType' });
    const programId = useWatch({ control, name: 'programId' });
    const classMeet = useWatch({ control, name: 'classMeet' });
    const duration = useWatch({ control, name: 'duration' });
    const schedules = useWatch({ control, name: 'schedules' }) || [];

    const getMeetingCount = () => {
        if (!selectedItem?.meetKey) return null;

        // "24x" → 24
        const totalHours = Number(selectedItem.meetKey.replace('x', ''));
        if (!totalHours) return null;

        if (duration === '1 Jam / Session') return totalHours;
        if (duration === '2 Jam / Session') return totalHours / 2;

        return null;
    };

    const { fields, append, remove } = useFieldArray({ control, name: 'schedules' });

    const getFilteredDayOptions = (index) => {
        const chosenDays = (schedules || []).map((s, i) => i !== index && s?.day?.value);
        return DAY_OPTIONS.filter((opt) => !chosenDays.includes(opt.value));
    };

    const resetDownstreamFromCity = () => {
        setValue('learningType', '');
        setValue('programId', '');
        setValue('classMeet', '');
        setValue('classMeetWeekly', '');
    };

    const resetDownstreamFromLearningType = () => {
        setValue('programId', '');
        setValue('classMeet', '');
        setValue('classMeetWeekly', '');
    };

    const resetDownstreamFromProgram = () => {
        setValue('classMeet', '');
        setValue('classMeetWeekly', '');
    };

    const getVisibleSteps = () => {
        const steps = [];

        if (!omitBasicsFields) {
            steps.push('classTypeCourse');
            steps.push('cityId');
            steps.push('learningType');
        }

        if (showRegularMeetFields && showPrice) {
            steps.push('classMeetWeekly');
        }

        if (showPrice) {
            steps.push('duration');
            steps.push('schedule');
        }

        return steps;
    };

    const getStepNumber = (key) => {
        const steps = getVisibleSteps();
        const index = steps.indexOf(key);
        return index === -1 ? null : index + 1;
    };

    return (
        <>
            {!omitBasicsFields && (
                <div>
                    <div className="d-flex form-row mb-2">
                        {/* <label className="form-label">Tipe Kelas</label> */}
                        <span className='me-2'>{getStepNumber('classTypeCourse')}.</span>
                        <select
                            className="form-select"
                            {...register('classTypeCourse', {
                                required: 'Pilih jenis kelas.',
                            })}
                        >
                            <option value="">Pilih Tipe Kelas</option>
                            <option value="t">Trial Class</option>
                            <option value="r">Regular Class</option>
                        </select>
                    </div>
                    <div>
                        {errors.classTypeCourse && (
                            <p className="text-danger text-sm mt-1">{errors.classTypeCourse.message}</p>
                        )}
                    </div>
                </div>
            )}

            {!omitBasicsFields && (
                <div>
                    <div className="d-flex form-row mb-2">
                        {/* <label className="form-label">Kota Tempat Kursus</label> */}
                        <span className='me-2'>{getStepNumber('cityId')}.</span>
                        <select
                            className="form-select"
                            aria-label="Kota kursus"
                            {...register('cityId', {
                                required: 'Kota wajib dipilih.',
                                onChange: () => {
                                    resetDownstreamFromCity();
                                    clearErrors(['cityId', 'learningType', 'programId', 'classMeet', 'classMeetWeekly']);
                                },
                            })}
                        >
                            <option value="">Pilih Kota Kursus</option>
                            {cityList.map((item, index) => (
                                <option key={item.id_area ?? index} value={item.id_area}>
                                    {item.area}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        {errors.cityId && (
                            <p className="text-danger text-sm mt-1">{errors.cityId.message}</p>
                        )}
                    </div>
                </div>
            )}

            {!omitBasicsFields && (
                <>
                    <div>
                        <div className="d-flex form-row mb-2">
                            {/* <label className="form-label">Tipe Pembelajaran</label> */}
                            <span className='me-2'>{getStepNumber('learningType')}.</span>
                            <select
                                className="form-select"
                                aria-label="Tipe pembelajaran"
                                {...register('learningType', {
                                    required: 'Tipe belajar wajib dipilih.',
                                    onChange: () => {
                                        resetDownstreamFromLearningType();
                                        clearErrors(['learningType', 'programId', 'classMeet', 'classMeetWeekly']);
                                    },
                                })}
                            >
                                <option value="">Pilih Tipe Pembelajaran</option>
                                {learningTypeList.map((item, index) => (
                                    <option key={`${item.schedule}-${index}`} value={item.schedule}>
                                        {item.schedule}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            {errors.learningType && (
                                <p className="text-danger text-sm mt-1">{errors.learningType.message}</p>
                            )}
                        </div>
                    </div>

                    <div className="form-row">
                        {/* <label className="form-label">Program</label> */}
                        <input type="hidden" {...register('programId', { required: true })} />
                        <input type="hidden" {...register('classMeet', { required: true })} />
                        {/* <p className="form-static mb-0">Program Terpilih :</p> */}
                        {/* <p className='mb-0'><strong>{selectedItem?.programName || '-'} {selectedItem?.classMeetLabel || '-'}</strong></p> */}

                        {/* <select
                            className="form-select"
                            aria-label="Kategori program"
                        >
                            <option value="">Pilih Program</option>
                            {programList.map( ( item ) => (
                                <option key={item.id_program} value={item.id_program}>
                                    {item.program?.nama_program}
                                </option>
                            ) )}
                        </select>
                        {errors.programId && (
                            <p className="text-danger text-sm mt-1">{errors.programId.message}</p>
                        )} */}
                    </div>
                </>
            )}

            <div className={`enrollment-transition ${showRegularMeetFields && showPrice ? 'is-visible' : ''}`}>
                {showRegularMeetFields && showPrice && (
                    <div>
                        {/* <div className="form-row">
                            <label className="form-label">Detail Pertemuan Program</label>
                            <input type="hidden" {...register('classMeet', { required: true })} />                        
                            <p className="form-static">
                                {selectedItem?.classMeetLabel || '-'}
                            </p>                        
                            <select
                                className="form-select"
                                aria-label="Detail pertemuan"
                            >
                                <option value="">Pilih Detail Pertemuan</option>
                                {classMeetList.map( ( item, index ) => (
                                    <option key={`${item.id_type}-${index}`} value={item.id_type}>
                                        {formatMeetDescription( item.type?.description )}
                                    </option>
                                ) )}
                            </select>
                            {errors.classMeet && (
                                <p className="text-danger text-sm mt-1">{errors.classMeet.message}</p>
                            )}
                        </div> */}

                        <div className="form-divider" />
                        <div className="d-flex form-row mb-2">
                            {/* <label className="form-label">Jumlah Pertemuan/Minggu</label> */}
                            <span className='me-2'>{getStepNumber('classMeetWeekly')}.</span>
                            <select
                                className="form-select"
                                aria-label="Jumlah pertemuan per minggu"
                                {...register('classMeetWeekly', {
                                    required: 'Jumlah pertemuan wajib dipilih.',
                                    onChange: (e) => {
                                        if (e.target.value) clearErrors('classMeetWeekly');
                                    },
                                })}
                            >
                                <option value="">Pilih Jumlah Pertemuan</option>
                                <option value="1x Meeting">1x Meeting</option>
                                <option value="2x Meeting">2x Meeting</option>
                                <option value="3x Meeting">3x Meeting</option>
                            </select>
                        </div>
                        <div>
                            {errors.classMeetWeekly && (
                                <p className="text-danger text-sm mt-1">{errors.classMeetWeekly.message}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className={`enrollment-transition ${showRegularMeetFields && showPrice ? 'is-visible' : ''}`}>
                {showRegularMeetFields && showPrice && (
                    <div>
                        <div className="form-row mb-2">
                            <span className='me-2'>{getStepNumber('duration')}.</span>
                            <label className="">Pilih Durasi Pembelajaran</label>
                            <div className="d-flex gap-2 ps-2">
                                <label className="d-flex align-items-center">
                                    <input
                                        type="radio"
                                        className="me-2"
                                        value="1 Jam / Session"
                                        {...register('duration', {
                                            required: 'Durasi pembelajaran wajib dipilih.',
                                        })}
                                    />
                                    1 Jam / Meeting
                                </label>
                                {duration === '1 Jam / Session' && (
                                    <span className="text-danger">{`${getMeetingCount() ?? '—'} kali Meeting`}</span>
                                )}
                            </div>
                            <div className="d-flex gap-2 ps-2">
                                <label className="d-flex align-items-center">
                                    <input
                                        type="radio"
                                        className="me-2"
                                        value="2 Jam / Session"
                                        {...register('duration', {
                                            required: 'Durasi pembelajaran wajib dipilih.',
                                        })}
                                    />
                                    2 Jam / Meeting
                                </label>
                                {duration === '2 Jam / Session' && (
                                    <span className="text-danger">{`${getMeetingCount() ?? '—'} kali Meeting`}</span>
                                )}
                            </div>
                            <div>
                                {errors.duration && (
                                    <p className="text-danger text-sm mt-1">{errors.duration.message}</p>
                                )}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="row mb-md-2">
                                {/* <div className="col-md-10">
                                    <label className="form-label">Pilih Hari dan Jam</label>
                                </div> */}
                                <div className="col text-end">
                                    <button
                                        type="button"
                                        onClick={() => append({ day: null, time: null })}
                                        className="btn-white"
                                        aria-label="Tambah jadwal"
                                    >
                                        <i className="fas fa-plus add-icon" />
                                    </button>
                                </div>
                            </div>

                            {fields.map((field, index) => (
                                <div className={`row ${index > 0 ? 'mt-2' : ''} gap-2`} key={field.id}>
                                    <div className='d-flex flex-column flex-md-row'>
                                        <span className='me-2'>{getStepNumber('schedule') + index}.</span>
                                        <div className="col-12 col-md-6 mb-3 mb-md-0 me-md-2">
                                            {/* <label htmlFor={`day-${index}`} className="mb-1 d-block">
                                                Hari
                                            </label> */}
                                            <Controller
                                                name={`schedules.${index}.day`}
                                                control={control}
                                                rules={{ required: 'Hari wajib dipilih.' }}
                                                render={({ field: f }) => (
                                                    <Select
                                                        inputId={`day-${index}`}
                                                        options={getFilteredDayOptions(index)}
                                                        value={f.value}
                                                        onChange={(opt) => {
                                                            f.onChange(opt);
                                                            clearErrors(`schedules.${index}.day`);
                                                        }}
                                                        className="basic-multi-select custom-select"
                                                        classNamePrefix="select"
                                                        placeholder="Hari..."
                                                    />
                                                )}
                                            />
                                            {errors.schedules?.[index]?.day && (
                                                <p className="text-danger text-sm mt-1">
                                                    {errors.schedules[index].day.message}
                                                </p>
                                            )}
                                        </div>
                                        <div className="col-12 col-md-5">
                                            {/* <label htmlFor={`time-${index}`} className="mb-1 d-block">
                                                Jam
                                            </label> */}
                                            <Controller
                                                name={`schedules.${index}.time`}
                                                control={control}
                                                rules={{ required: 'Jam wajib dipilih.' }}
                                                render={({ field: f }) => (
                                                    <DatePicker
                                                        id={`time-${index}`}
                                                        selected={f.value}
                                                        onChange={(date) => {
                                                            f.onChange(date);
                                                            clearErrors(`schedules.${index}.time`);
                                                        }}
                                                        showTimeSelect
                                                        showTimeSelectOnly
                                                        timeIntervals={30}
                                                        dateFormat="HH:mm"
                                                        minTime={new Date(0, 0, 0, 10, 0)}
                                                        maxTime={new Date(0, 0, 0, 19, 0)}
                                                        showTimeCaption={false}
                                                        className="custom-clock form-control"
                                                        placeholderText='Jam...'
                                                    />
                                                )}
                                            />
                                            {errors.schedules?.[index]?.time && (
                                                <p className="text-danger text-sm mt-1">
                                                    {errors.schedules[index].time.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {index > 0 && (
                                        <div className="col-auto d-flex align-items-end pb-1">
                                            <button
                                                type="button"
                                                onClick={() => remove(index)}
                                                className="btn btn-link text-danger p-0"
                                                aria-label="Hapus jadwal"
                                            >
                                                <i className="fas fa-minus" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            <p className="small mt-2 mb-0">
                                <b>Note: </b>
                                Silahkan tambahkan jadwal kosong kamu sebanyak mungkin, dengan cara klik tombol tambah
                                (warna merah).
                            </p>
                        </div>
                    </div>
                )}            
            </div>

        </>
    );
};

export default EnrollmentBookingFields;